// ===========================================
// Cursor Usage Upload Component (Simplified)
// ===========================================
// Simple drag & drop upload for Cursor billing CSV files
// Charts and visualizations are now in the main Analytics page

import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Upload, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import './CursorUsageUpload.scss';

// ===========================================
// Component accepts:
// - onDataRefresh: callback for notifying parent when data changes
// ===========================================
const CursorUsageUpload = ({ onDataRefresh }) => {
  // Get upload function from auth context
  const { uploadCursorUsage, refreshUser } = useAuth();
  
  // Component state
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const fileInputRef = useRef(null);

  // ===========================================
  // File Handling
  // ===========================================
  
  // Process selected CSV file
  const handleFileSelect = async (file) => {
    // Validate file type
    if (!file || !file.name.endsWith('.csv')) {
      setMessage({ type: 'error', text: 'Please upload a CSV file' });
      return;
    }

    setUploading(true);
    setMessage({ type: '', text: '' });

    try {
      // Read file contents as text
      const csvData = await file.text();
      
      // Upload to API for processing
      const result = await uploadCursorUsage(csvData);
      
      if (result.success) {
        // Show success message with new/duplicate counts
        const { newEvents, duplicatesSkipped, totalProcessed } = result.data;
        let messageText = `Processed ${totalProcessed.toLocaleString()} events`;
        if (newEvents > 0) {
          messageText += `: ${newEvents} new`;
        }
        if (duplicatesSkipped > 0) {
          messageText += `, ${duplicatesSkipped} duplicates skipped`;
        }
        setMessage({ type: 'success', text: messageText });
        
        // Refresh user data after successful upload
        await refreshUser();
        
        // Notify parent component to refetch data
        if (onDataRefresh) {
          onDataRefresh();
        }
      } else {
        setMessage({ type: 'error', text: result.error });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to read file' });
    }

    setUploading(false);
  };

  // Handle file input change
  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  // ===========================================
  // Drag and Drop Handlers
  // ===========================================
  
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFileSelect(file);
  };

  return (
    <div className="cursor-usage cursor-usage--upload-only">
      {/* Upload Zone - drag and drop or click to browse */}
      <div 
        className={`cursor-usage__dropzone ${dragActive ? 'cursor-usage__dropzone--active' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleInputChange}
          hidden
        />
        
        {uploading ? (
          <Loader className="cursor-usage__icon animate-spin" size={24} />
        ) : (
          <Upload className="cursor-usage__icon" size={24} />
        )}
        
        <div className="cursor-usage__dropzone-text">
          <span className="cursor-usage__dropzone-title">
            {uploading ? 'Processing CSV...' : 'Upload Cursor Billing CSV'}
          </span>
          <span className="cursor-usage__dropzone-hint">
            Drag & drop or click to browse
          </span>
        </div>
      </div>

      {/* Status Message */}
      {message.text && (
        <div className={`cursor-usage__message cursor-usage__message--${message.type}`}>
          {message.type === 'success' ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
          {message.text}
        </div>
      )}

      {/* Help text with instructions */}
      <div className="cursor-usage__help">
        <p>
          Export from <strong>Cursor Settings → Billing → Download CSV</strong>
        </p>
      </div>
    </div>
  );
};

export default CursorUsageUpload;
